import React from 'react';

// Collapsible section header for the leg sheet. Same convention as the trip
// form's optional-details block (full-width button, aria-expanded, chevron
// rotating 180deg). Every section of the sheet collapses, closed by default,
// so the sheet opens on the transport picker and the booking button and you
// open only what you need (Paulo 2026-07-30, extended to all sections
// 2026-09-28). Used by LegSheet and NightTrainSheetBlock.
//
// Both buttons carry `data-share-ok`: opening a section edits nothing, so
// read-only shared views let it through (pages/SharedJourneyPage.tsx).
// Conventions: docs/codebase/ui-conventions.md.

const heading: React.CSSProperties = { fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 15, color: '#16241d', margin: 0 };

interface SectionToggleProps {
  title: string;
  /** Decorative lead before the title (an emoji), hidden from screen readers. */
  lead?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  /** Sits between the title and the chevron (a status pill, an info button). */
  children?: React.ReactNode;
}

const SectionToggle: React.FC<SectionToggleProps> = ({ title, lead, open, onToggle, children }) => (
  // Split layout on purpose: `children` (e.g. the Planned pill) is a real
  // button, and a button inside a button is invalid HTML with unreliable
  // clicks. So the title and the chevron are two sibling toggles with the
  // children between them, rather than one wrapping button.
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: open ? 11 : 0 }}>
    <button
      type="button"
      data-share-ok
      onClick={onToggle}
      aria-expanded={open}
      style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
    >
      <span style={heading}>{lead != null && <span aria-hidden>{lead} </span>}{title}</span>
    </button>
    {children}
    <button
      type="button"
      data-share-ok
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
      style={{ flex: 'none', display: 'grid', placeItems: 'center', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <svg
        width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden
        style={{ color: '#6c7872', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}
      >
        <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  </div>
);

export default SectionToggle;
